import { useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { UploadCloud, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export default function LeadCsvImport({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  function reset() {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  async function downloadTemplate() {
    try {
      const r = await api.get("/crm/leads/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  }

  async function upload() {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/crm/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} lead(s)`);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1B2A]">Import leads from CSV</DialogTitle>
          <DialogDescription>
            Upload a UTF-8 CSV. Required columns: <b>org_name</b>, <b>contact_name</b>, <b>contact_email</b>.
            Duplicate emails are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-dashed border-slate-300 bg-[#E6F4F1]/40 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-[#0D1B2A]">
              <FileText className="w-4 h-4 text-[#1A7F6E]" />
              Not sure of the format?
            </div>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="csv-template-download">
              <Download className="w-4 h-4 mr-1.5" /> Template
            </Button>
          </div>

          <label
            htmlFor="csv-file"
            className="flex flex-col items-center justify-center gap-2 rounded-md border border-slate-300 border-dashed py-8 cursor-pointer hover:border-[#1A7F6E]"
            data-testid="csv-drop"
          >
            <UploadCloud className="w-6 h-6 text-slate-400" />
            <span className="text-sm text-slate-600">
              {file ? file.name : "Click to choose a .csv file"}
            </span>
            <input
              ref={inputRef}
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="csv-file-input"
            />
          </label>

          {result && (
            <div className="rounded-md border border-slate-200 p-3 text-sm space-y-1" data-testid="csv-import-result">
              <div className="text-emerald-700">
                Created: <b>{result.created}</b>
              </div>
              <div className="text-slate-600">
                Skipped (duplicates): <b>{result.skipped_duplicates}</b>
              </div>
              {result.errors?.length > 0 && (
                <div className="text-rose-700">
                  <div>Errors: <b>{result.errors.length}</b></div>
                  <ul className="list-disc pl-5 max-h-32 overflow-auto text-xs mt-1">
                    {result.errors.map((er) => (
                      <li key={`err-${er.row}`}>Row {er.row}: {er.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="csv-import-cancel">
            Close
          </Button>
          <Button
            onClick={upload}
            disabled={!file || busy}
            className="bg-[#1A7F6E] hover:bg-[#136053] text-white"
            data-testid="csv-import-submit"
          >
            {busy ? "Uploading…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
